<template>
  <div
    :key="renderTrigger"
    ref="dropdown"
    class="dropdown"
    data-cy="profile-dropdown"
  >
    <ul class="dropdown-list px-0 py-2 ma-0">
      <template v-if="showOrgOptions">
        <profile-dropdown-option
          v-if="activeOrg != null"
          data-cy="active-org"
          :main-text="activeOrg.value"
          :max-text-length="maxTextLength"
          :subtext="activeOrg.subtext"
          :clickable="false"
          :bold="true"
        />

        <profile-dropdown-option
          v-if="maySwitchToProduction"
          main-text="Switch to production"
          icon="tab-arrows"
          data-cy="switch-environment"
          :show-avatar="false"
          @click.native="handleOptionClick(switchToProductionOption)"
        />

        <profile-dropdown-option
          v-if="maySwitchToStaging"
          main-text="Switch to staging"
          icon="tab-arrows"
          data-cy="switch-environment"
          :show-avatar="false"
          @click.native="handleOptionClick(switchToStagingOption)"
        />

        <div
          v-if="hasOtherOrgs"
          class="dropdown-section"
        >
          <ngc-divider />
          <p class="dropdown-subtitle">
            Organizations
          </p>
          <profile-dropdown-option
            v-for="(org, index) in otherOrgs"
            :key="index"
            :main-text="org.value"
            :max-text-length="maxTextLength"
            :subtext="org.subtext"
            @click.native="handleOptionClick(org)"
          />
        </div>
      </template>

      <div
        v-if="userGuid"
        class="dropdown-section"
      >
        <ngc-divider v-if="showOrgOptions" />
        <p class="dropdown-subtitle">
          Profile
        </p>
        <profile-dropdown-option
          :image="profilePictureUrl"
          :main-text="userName"
          :clickable="false"
          :subtext="roleType"
        />
        <profile-dropdown-option
          :main-text="'My profile'"
          :icon="'person'"
          :show-avatar="false"
          @click.native="handleOptionClick(profileSettingsOption)"
        />
      </div>

      <ngc-divider />

      <profile-dropdown-option
        main-text="Sign out"
        :show-avatar="false"
        @click.native="handleOptionClick(signOutOption)"
      />
    </ul>
  </div>
</template>

<script>
import { userOrgs } from '../graphql/signing_room_queries.gql'
import ProfileDropdownOption from './profileDropdownOption.vue'
import { mapGetters, mapMutations } from 'vuex'
import snSigningRoomService from '../../services/snSigningRoomService'
import cookie from 'js-cookie'
import _ from 'lodash'

export default {
  name: 'ProfileDropdown',
  components: {
    ProfileDropdownOption
  },
  props: {
    userName: {
      type: String,
      default: '',
      required: false
    },
    userGuid: {
      type: String,
      default: '',
      required: true
    },
    profilePictureUrl: {
      type: String,
      default: '',
      required: false
    },
    isShown: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      orgOptions: [],
      signOutOption: { value: 'sign_out', action: 'logout' },
      switchToProductionOption: { value: false, action: 'set_is_staging' },
      switchToStagingOption: { value: true, action: 'set_is_staging' },
      profileSettingsOption: { value: 'profile_settings', action: 'profile_settings' },
      profileSettingsVisible: false,
      renderTrigger: 0,
      maxTextLength: 20,
      mayClose: false // prevents click event that opened the dropdown from immediately closing the dropdown
    }
  },
  computed: {
    ...mapGetters(['user', 'isStaging', 'orgId', 'viewAsCompany', 'appInitialized']),
    activeOrg () {
      return this.orgOptions.find(opt => opt.guid === this.orgId)
    },
    otherOrgs () {
      return this.orgOptions
        .filter(opt => opt.guid !== this.orgId)
        .sort((a, b) => a.name - b.name)
    },
    hasOtherOrgs () {
      return this.otherOrgs.length > 0
    },
    maySwitchToStaging () {
      return !this.isStaging && (snSigningRoomService.hasPermission(this.user, 'stagingEnabled'))
    },
    maySwitchToProduction () {
      return this.isStaging && (snSigningRoomService.hasPermission(this.user, 'productionEnabled'))
    },
    showOrgOptions () {
      return !this.user.isSnAdmin || this.viewAsCompany
    },
    companyGuid () {
      if (this.user.isSnAdmin) {
        return this.viewAsCompany ? this.viewAsCompany.guid : undefined
      }
      return undefined
    },
    roleType () {
      if (this.user.accountType) {
        return _.capitalize(this.user.accountType.replaceAll('_', ' '))
      }
      return ''
    }
  },
  created () {
    document.addEventListener('click', this.clickAway)
    this.getOrgs()
  },
  beforeDestroy () {
    document.removeEventListener('click', this.clickAway)
  },
  methods: {
    ...mapMutations(['setIsStaging', 'setOrgId']),
    handleOptionClick (option) {
      // reroute user to current base route if on details pages
      const matchedPath = this.$route.matched[0].path
      const basePath = matchedPath.split(':')[0]

      switch (option.action) {
        case 'switch_org':
          this.switchOrg(option.guid)
          this.close()
          if (basePath !== matchedPath) {
            this.$router.push(basePath)
          }
          break
        case 'set_is_staging':
          this.switchEnvironment(option.value)
          this.close()
          if (basePath !== matchedPath) {
            this.$router.push(basePath)
          }
          break
        case 'profile_settings':
          this.$emit('openProfileSettings')
          this.close()
          break
        case 'logout':
          this.$emit('logout')
          break
        default:
          break
      }
    },
    async getOrgs () {
      if (!this.appInitialized) {
        return
      } else if (this.user.isSnAdmin && this.viewAsCompany == null) {
        this.setOrgId(null)
        return
      }

      const { data } = await this.$apollo.query({
        query: userOrgs,
        variables: {
          companyGuid: this.viewAsCompany?.guid
        }
      })

      if (data.userOrgs) {
        const orgs = data.userOrgs

        this.orgOptions = orgs.map(org => {
          return { guid: org.guid, value: org.name, subtext: '', action: 'switch_org', active: org.active }
        })

        const orgIds = orgs.map(org => org.guid)
        const cookieOrgId = cookie.get('srm_org_id') || cookie.get('temp_org_guid')

        if (orgIds.includes(cookieOrgId)) {
          this.setOrgId(cookieOrgId)
        } else if (orgIds.length > 0) {
          this.setOrgId(orgIds[0])
        } else {
          this.setOrgId(null)
        }
      }
    },
    switchOrg (newOrgId) {
      this.setOrgId(newOrgId)
    },
    switchEnvironment (isStaging) {
      this.setIsStaging(isStaging)
    },
    toggleProfileSettings () {
      this.profileSettingsVisible = !this.profileSettingsVisible
    },
    clickAway (event) {
      if (this.isShown) {
        if (this.mayClose) {
          const { clientX, clientY } = event
          const bounds = this.$refs.dropdown.getBoundingClientRect()

          if (!this.isWithin(clientX, clientY, bounds)) {
            this.close()
          }
        } else {
          this.mayClose = true
        }
      }
    },
    close () {
      this.mayClose = false
      this.$emit('close')
    },
    isWithin (clientX, clientY, bounds) {
      if (clientX >= bounds.x && clientX <= (bounds.x + bounds.width)) {
        if (clientY >= bounds.y && clientY <= (bounds.y + bounds.height)) {
          return true
        }
      }
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
.dropdown {
  border-radius: 0;
  width: 250px;
  // Replaces the .v-card / .sn-shadow-depth-1 pair the root div used to carry.
  // --shadow-1-card has the same offsets and blurs as $sn-shadow-depth-1 and
  // the same #1F1F1F base, differing only in the alpha of its second and third
  // layers. Dropping .v-card also retires the max-width: none that had to undo
  // its max-width: 100%, which resolved against the avatar anchor and collapsed
  // this to ~48px.
  background: var(--color-surface-primary, #fff);
  box-shadow: var(--shadow-1-card);
  position: absolute;
  right: -5px;
  // 100% is the avatar box's height, so the card keeps a constant gap below it
  // whatever size the avatar is. The old 70px was measured from .header-box's
  // top, and the mr-8/mt-2 utilities that used to pull the card in from that
  // full-width anchor are gone - against the avatar they only pushed it out of
  // alignment.
  top: calc(100% + 8px);
  z-index: 1000;
  user-select: none;

  &-list {
    display: flex;
    flex-direction: column;
    list-style-type: none;
    text-align: left;

    &-item {
      white-space: nowrap;
      padding: 10px 16px;
      margin-left: -1px;
      line-height: 24px;
      cursor: pointer;

      &:hover {
        background: rgba(15, 95, 94, 0.05);
      }
    }
  }
  // Carried .sn-subtitle-2 in the template; its 15px/22px bold is Gator's
  // body-2. $sn-gray-04 was #BFBFBF, which --color-neutral-40 matches exactly -
  // the semantic token for de-emphasised text, --color-text-secondary, is
  // considerably darker and would change how this reads.
  &-subtitle {
    text-transform: uppercase;
    font-family: var(--font-family-body);
    font-size: var(--font-size-body-2);
    line-height: var(--line-height-body-2);
    font-weight: var(--font-weight-bold);
    color: var(--color-neutral-40);
    margin: 12px 16px 4px 16px;
  }
}

// The ngc-divider host is inline by default, the same reason .side-menu-divider
// needs this in signingRoomApp.vue.
ngc-divider {
  display: block;
}

.dropdown-section {
  display: flex;
  flex-direction: column;
}

</style>
